import React, {
	type ComponentType,
	type PropsWithChildren,
	useCallback,
	useEffect,
	useState,
} from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { AISources, useAnalytics } from '../../common';
import { SainAction, useSAINUsageCriteriaMet } from '../../common/controller/analytics';
import { AIAnswerBottomBookend } from '../../common/ui/ai-answer-bookends/ai-answer-bottom-bookend';
import { AIAnswerTopBookend } from '../../common/ui/ai-answer-bookends/ai-answer-top-bookend';
import { AIAnswerBody } from '../ai-answer-body';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerFooter } from '../ai-answer-footer';
import { AIAnswerLoadingDialog } from '../ai-answer-loading/ai-answer-loading-dialog';
import {
	aiAnswerCollapseClicked,
	aiAnswerExpandClicked,
	aiAnswerExpandShown,
	onAIAnswerSourceClick,
} from '../analytics';
import { NLPSearchResultFormat } from '../types';

import { Collapse } from './collapse';
import { AIAnswerSearchBorder } from './styled';
import { useSmartAnswerCardFeedbackOptions } from './use-smart-answer-card-feedback-options';

const MAX_SOURCES = 4;

const spacerStyles = xcss({
	height: '14px',
	paddingTop: 'space.250',
});

const errorContainerStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.200',
});

const sourcesContainerStyles = xcss({
	paddingTop: '0',
	paddingRight: 'space.300',
	paddingBottom: 'space.100',
	paddingLeft: 'space.300',
});

type LegacySmarAnswerCardProps = {
	LoadingWrapper: ComponentType<PropsWithChildren<{}>>;
	DialogWrapper: ComponentType<PropsWithChildren<{}>>;
};

// TODO remove this file when cleaning up `kd_sain_enable_expand_when_streaming`
/**
 * @deprecated This component is the old LegacySmartAnswerCard that didn't support expansion while streaming
 * use LegacySmartAnswerCard instead
 */
export const LegacySmartAnswerCardWithoutStreamingExpand = ({
	LoadingWrapper,
	DialogWrapper,
}: LegacySmarAnswerCardProps) => {
	const { commonAttributes, callbacks, loading, isReadingAids, onClose, hasError, showBookends } =
		useAIAnswerContext();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { trackAIResultAction } = useAIEventsInstrumentation();
	const { answerFormat, query } = commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const [collapsed, setCollapsed] = useState(true);
	const isQuickSearch = Boolean(!isReadingAids && onClose);
	const feedbackOptions = useSmartAnswerCardFeedbackOptions();

	// reset on each new query
	useEffect(() => {
		setCollapsed(true);
	}, [query]);

	const toggleCollapsedState = () => {
		if (collapsed) {
			fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED, commonAttributes });
		}
		setCollapsed(!collapsed);
	};

	const analyticsCallbacks = {
		onExpandedByDefault: () => fireAnalyticsEvent(aiAnswerExpandShown(commonAttributes)),
		onExpandClicked: () => fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes)),
		onCollapseClicked: () => fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes)),
	};

	const sourceClickCallback = useCallback(
		(index: number, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					...commonAttributes,
				}),
			);
			fg('kd_enable_new_usage_tracking')
				? fireSAINUsageEvent({ metCriteria: SainAction.SOURCE_CLICKED, commonAttributes })
				: trackAIResultAction(SainAction.SOURCE_CLICKED);
			callbacks?.onAIAnswerSourceClick?.();
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, callbacks, trackAIResultAction],
	);

	if (loading) {
		return (
			<LoadingWrapper>
				<AIAnswerLoadingDialog />
			</LoadingWrapper>
		);
	}

	let aiAnswerContent = (
		<Collapse
			collapsed={collapsed}
			toggleCollapsedState={toggleCollapsedState}
			expandWhenClickedAnywhere
			isLoading={false}
			callbacks={analyticsCallbacks}
		>
			<AIAnswerBody />
		</Collapse>
	);

	if (hasError) {
		aiAnswerContent = (
			<Box xcss={errorContainerStyles}>
				<AIAnswerBody />
			</Box>
		);
	} else if (isQuickSearch || answerFormat === NLPSearchResultFormat.JSON) {
		aiAnswerContent = (
			<>
				<Box xcss={spacerStyles}></Box>
				<AIAnswerBody />
			</>
		);
	}

	return (
		<DialogWrapper>
			{showBookends ? (
				<AIAnswerTopBookend showFeedbackButtons={true} feedbackOptions={feedbackOptions} />
			) : null}
			<AIAnswerSearchBorder isLoading={false} isStreaming={false}>
				{aiAnswerContent}
				{!hasError && (
					<Box xcss={sourcesContainerStyles}>
						<AISources
							sources={commonAttributes.sources}
							searchSessionId={commonAttributes.searchSessionId}
							onClickHandler={sourceClickCallback}
							limit={MAX_SOURCES}
							enableSmartLinkSources={true}
						/>
					</Box>
				)}
				<AIAnswerFooter />
			</AIAnswerSearchBorder>
			{showBookends ? <AIAnswerBottomBookend /> : null}
		</DialogWrapper>
	);
};
